<script setup lang="ts">
/**
 * One item of the icon rail: a glyph with a label beneath it.
 *
 * ## Why this is in `base/` and not in `layout/`
 *
 * It is a control, and `components/base/` is the only tier allowed to draw one — a rule
 * that exists so every control in the panel gets the same focus treatment and the same
 * token palette. The rail is a design-system element in its own right: it has a dedicated
 * token family (`--rail-*`), and those tokens are **theme-invariant**, which no other
 * surface in the system is. Drawing it anywhere else would be the first exception, and the
 * first exception is how a rule stops being checkable.
 *
 * It depends on nothing above it, which is the actual contract of this tier.
 */
defineProps<{ active: boolean }>()

const emit = defineEmits<{ click: [] }>()
</script>

<template>
  <button
    type="button"
    :aria-current="active ? 'page' : undefined"
    :class="[
      // Full rail width, square-edged: the active fill reads as a selected SECTION of the
      // rail rather than a chip floating inside it, and a ten-letter label has the whole
      // 92 px rather than a 64 px box it overflowed on both sides.
      'relative flex w-full flex-col items-center gap-1 px-1 py-2.5',
      'transition-colors duration-(--motion-fast)',
      active ? 'bg-rail-active text-rail-accent' : 'text-rail-ink hover:bg-rail-active',
    ]"
    @click="emit('click')"
  >
    <!--
      The accent edge, drawn only on the active section.

      A bar that slid between items would have to measure their positions and keep that
      measurement correct through a collapsing rail — real complexity for the same read.
      This grows out of its own item instead: the eye registers which section took the
      fill, which is the only question the motion is answering.
    -->
    <span
      v-if="active"
      class="rail-accent absolute top-1 bottom-1 left-0 w-0.5 rounded-full bg-rail-accent"
      aria-hidden="true"
    />
    <slot />
  </button>
</template>

<style scoped>
@keyframes rail-accent-in {
  from {
    opacity: 0;
    transform: scaleY(0.35);
  }
}

.rail-accent {
  animation: rail-accent-in var(--motion-overlay) var(--motion-ease-out);
}

@media (prefers-reduced-motion: reduce) {
  .rail-accent {
    animation: none;
  }
}
</style>
